import { useStore } from "@tanstack/react-store"
import { computed, signal } from "~/sdk/signal"
import { Product } from "@decocms/apps-commerce/types"

interface ExtendedProduct {
    price: number
    discount: number
    quantity?: number
}

export const selectedProducts = signal<Map<string, Product & ExtendedProduct>>(new Map())

// React-reactive readers. The underlying `signal()` is backed by
// @tanstack/store and does NOT auto-subscribe on `.value` reads inside
// a render function (unlike @preact/signals in prod). Components that
// need to re-render when the atom changes MUST call these hooks instead
// of reading `.value` directly — otherwise clicking +/- in the variant
// selector mutates the Map but never triggers a re-render, and the
// BuyButton sees an empty selection forever ("Selecione um produto
// primeiro" even after picking variants).
//
// The non-hook `subtotal` / `itemsCount` computed exports below stay
// for non-component callers (analytics, server, peek).
// @tanstack/react-store@0.9 requires (atom, selector) — the selector is
// `(s) => s` here because we want the full Map. The Store type carries
// BaseAtom-compatible `.get()` and `.subscribe()` at runtime; cast to
// `any` to satisfy the AnyAtom constraint (the type system rejects
// Store as BaseAtom even though the contract matches).
type SelectedMap = Map<string, Product & ExtendedProduct>

export function useSelectedProducts(): SelectedMap {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return useStore(selectedProducts.store as any, (s: SelectedMap) => s)
}

export function useSubtotal(): number {
    const products = useSelectedProducts()
    return [...products.values()].reduce(
        (acc, { price, quantity }) => acc + price * (quantity ?? 1),
        0,
    )
}

export function useItemsCount(): number {
    const products = useSelectedProducts()
    return [...products.values()].reduce(
        (acc, { quantity }) => acc + (quantity ?? 1),
        0,
    )
}

export const subtotal = computed(() =>
    [...selectedProducts.value.values()].reduce(
        (acc, { price, quantity }) => acc + price * (quantity ?? 1),
        0,
    )
)

export const itemsCount = computed(() =>
    [...selectedProducts.value.values()].reduce((acc, { quantity }) => acc + (quantity ?? 1), 0)
)
